<template>
  <section ref="defaultRef" class="min-h-screen flex flex-col">
    <!-- Header 仅 H5；App 内由原生导航，不渲染 -->
    <ClientOnly>
      <Header v-if="showH5Header" class="888-s" />
    </ClientOnly>
    <!-- 固定顶栏占位：透明头/相对定位头不加，避免 VIP/首页被顶开 -->
    <div
      v-if="needHeaderSpacer"
      class="h-12 w-full shrink-0"
      aria-hidden="true"
    />

    <!-- 页面主体始终 SSR/hydrate，避免 App 识别后整页 ClientOnly 清空导致闪白 -->
    <section class="flex flex-col relative z-10" :class="sectionClass">
      <slot />
    </section>

    <!-- 原 App 分支会始终出 Footer；H5 仍按路由隐藏 -->
    <Footer v-if="isAPP || !isNoFooter" class="mt-auto" />
    <BackTop />

    <!-- 滑块验证组件 -->
    <Verify
      v-show="captchaState.visible"
      captcha-type="blockPuzzle"
      :img-size="{ width: '310px', height: '155px' }"
      :visible="captchaState.visible"
      @success="handleCaptchaSuccess"
      @close="captchaState.visible = false"
    />
  </section>
</template>

<script lang="ts" setup>
  import Footer from './Footer.vue'
  import Header from './Header.vue'
  import { computed } from 'vue'
  import Verify from '~/components/Verify2/index.vue'
  import { ensureVipcodeRouteHook, ensureVantRouteHook, isHomeRoute } from '~/utils/homeRoute'
  import {
    ensureVantCssForRoute,
    loadFullVantCss,
  } from '~/utils/vantCss'
  // 从全局 css[] 下沉到布局，减轻首页 entry 阻塞；vipcode 按路由挂载
  import '~/assets/iconfont/iconfont.css'

  let vipcodeCssLoading: Promise<unknown> | null = null
  const ensureVipcodeCss = () => {
    if (vipcodeCssLoading) return vipcodeCssLoading
    vipcodeCssLoading = import('~/assets/css/vipcode.css').catch(() => {
      vipcodeCssLoading = null
    })
    return vipcodeCssLoading
  }

  const scheduleIdle = (fn: () => void, timeout = 2000) => {
    if (!import.meta.client) return
    const ric = (
      window as Window & {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number
      }
    ).requestIdleCallback
    if (typeof ric === 'function') {
      ric(fn, { timeout })
      return
    }
    if (document.readyState === 'complete') {
      setTimeout(fn, 1)
      return
    }
    window.addEventListener('load', () => setTimeout(fn, 1), { once: true })
  }

  // ============================================
  // 滑块验证相关 - 使用与 jcFetch.ts 相同的 useState key
  // ============================================
  const { $processCaptchaSuccess } = useNuxtApp()
  const captchaState = useState('captchaState', () => ({
    visible: false,
    queue: [] as any[],
    isVerifying: false,
    currentResolve: null as ((uuid: string) => void) | null,
    currentReject: null as ((error?: any) => void) | null,
  }))

  function handleCaptchaSuccess(result: { uuid: string; code: string }) {
    console.log('🎯 滑块验证成功:', result)
    if ($processCaptchaSuccess) {
      ;($processCaptchaSuccess as Function)(result?.uuid)
    }
  }

  watch(
    () => captchaState.value.visible,
    (newVal) => {
      console.log('🔔 captchaState.visible 变化:', newVal)
    },
  )

  const systemStore = useMySystemStore()
  const isAPP = computed(() => systemStore.isAPP)
  const defaultRef = ref()
  const route = useRoute()
  const router = useRouter()
  const onHome = isHomeRoute(route)

  // Vant：首页按需，其它页（含 /company/）全量；均在 setup await，保证 SSR 有样式
  await ensureVantCssForRoute(onHome)

  // 名录等：setup 阶段就拉 vipcode（含 SSR），避免会员标 FOUC
  if (!onHome) {
    await ensureVipcodeCss()
  }
  const headStyle = computed(() => {
    return String(route?.meta?.headStyle || '')
  })
  const isNoHeader = computed(() => {
    return systemStore.noHeader || isAPP.value
  })

  const routerName = computed(() => route?.name)
  const NoFooterRouteNameList = ['InquiryOffer']
  const isNoFooter = computed(() =>
    NoFooterRouteNameList.includes(routerName.value as string),
  )

  const detectNativeApp = () => {
    if (!import.meta.client) return false
    return (
      Boolean(
        (window as any)?.webkit?.messageHandlers?.jcAppMessageHandler
          ?.postMessage,
      ) || Boolean((window as any)?.jcAppMessageHandler?.postMessage)
    )
  }

  // 仅客户端确认是 H5 后才预留 Header 高度。
  // SSR 默认 isAPP=false，若直接留顶距而 Header 又是 ClientOnly，
  // App 首屏会先露出一条顶白条；Pinia hydrate 还可能把 isAPP 盖回 false。
  const allowHeaderOffset = ref(false)

  const syncAppFlag = () => {
    if (!import.meta.client) return
    if (detectNativeApp()) {
      systemStore.changeAppFlag(true)
      allowHeaderOffset.value = false
      return
    }
    allowHeaderOffset.value = true
  }

  syncAppFlag()

  const showH5Header = computed(
    () => allowHeaderOffset.value && !isNoHeader.value && !isAPP.value,
  )

  // 与原先 pt-[48px] 条件一致：普通固定顶栏才占位
  const needHeaderSpacer = computed(() => {
    if (!showH5Header.value) return false
    if (systemStore.headerIsWhite) return false
    if (headStyle.value.toLowerCase().includes('relative')) return false
    return true
  })

  const sectionClass = computed(() => {
    // 顶距改由 needHeaderSpacer 的 h-12 负责，避免与 fixed Header 不同步
    return '!pt-0'
  })

  useHead(() => {
    return {
      link: route.meta.noPc
        ? []
        : [
            {
              rel: 'canonical',
              id: 'canonical',
              href: `https://www.jctrans.com${
                route.fullPath.startsWith('/')
                  ? route.fullPath
                  : '/' + route.fullPath
              }`,
            },
          ],
    }
  })
  onMounted(() => {
    // Pinia SSR hydrate 之后再同步一次，避免 isAPP 被服务端 false 覆盖
    syncAppFlag()

    // 首页：idle 再挂 vipcode + 全量 Vant（下方 swipe/back-top/dialog 等）
    if (isHomeRoute(route)) {
      scheduleIdle(() => {
        ensureVipcodeCss()
        loadFullVantCss()
      })
    }

    watch(
      () => route.path,
      () => {
        if (route.meta.noPc) {
          setTimeout(() => {
            document
              .querySelectorAll('link[rel="canonical"]')
              .forEach((tag) => tag.remove())
          }, 50)
        }
      },
      { immediate: true },
    )
  })

  ensureVipcodeRouteHook(router, ensureVipcodeCss)
  ensureVantRouteHook(router, loadFullVantCss)

  watch(
    () => route.query.appPageReload,
    async (reload) => {
      console.log('🍉 ~ default.vue:89 ~ reload:', reload)
      if (reload && systemStore.isAPP) {
        const { appPageReload, ...rest } = route.query
        await router.replace({
          path: route.path,
          query: { ...rest },
        })
      }
    },
    {
      immediate: true,
    },
  )
</script>

<style></style>
